/* eslint-disable complexity */
import { useState } from "react";
import { Card, CardProps } from "@imminently/react/ui/core/card";
import { Text } from "@imminently/react/ui/core/text";
import { Drawer, Skeleton, Stack } from "@mui/material";

import { HeartIcon } from "@/assets/icons";
import NextObservationsPanel from "@/components/NextObservationsPanel";
import { formatDuration, formatName, getTimeFromNow } from "@/global";
import { useLiveNextObservations } from "@/services";
import { Theme, css } from "@/theme";
import { WORK_OFFLINE } from "@/resources/constants";
import { createMockNextObservations } from "@/pages/home/patient-list/mocking/patient-list-mocking";
import { useMemo } from "react";
import { PatientEncounter } from "@/resources/types";
import { usePatientList } from "@/pages/home/patient-list/patient-slice";
import PatientName from "./Patient/PatientName";

// Minutes till each Observation
const OBSERVATION_INTERVAL = 30;

const styles = {
  card: (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    min-width: 15rem;
    max-width: 20rem;
    flex: 0 0 auto;
    flex-shrink: 0;
    box-shadow: none;
    cursor: pointer;
    background: linear-gradient(
        to right,
        ${theme.palette.common.green[100]} 0%,
        ${theme.palette.common.green[100]} var(--fill),
        ${theme.palette.common.green[50]} var(--fill),
        ${theme.palette.common.green[50]} 100%
      );

    &[data-due="true"] {
      background: ${theme.palette.common.red[100]};
      border-color: ${theme.palette.common.red[100]};
    }

    &[data-small="true"] {
      flex-flow: row nowrap;
      align-items: center;
      padding: 0.25rem 1rem;
      border-radius: 1rem;
      gap: 0.5rem;
      min-width: 12rem;
      max-width: 18rem;
      flex-shrink: 0;
    }

    @media (max-width: 768px) {
      min-width: 10rem;
      max-width: 15rem;

      &[data-small="true"] {
        min-width: 8rem;
        max-width: 12rem;
      }
    }
  `,
  skeleton: (theme: Theme) => css`
    min-width: 15rem;
    border: 1px solid ${theme.palette.primary[100]};
    box-shadow: none;
  `,
  skeletonSmall: () => css`
    min-width: 15rem;
    border: none;
    box-shadow: none;
  `,
};

const ObservationSkeleton = ({ small = false }: { small?: boolean }) =>
  small ? (
    <Card css={styles.skeletonSmall}>
      <Stack direction="row" px={2} py={1} gap={1}>
        <Skeleton variant="rounded" width="1rem" height="1rem" />
        <Skeleton variant="text" width="6rem" height="1rem" />
        <Skeleton variant="text" width="10rem" height="1rem" />
      </Stack>
    </Card>
  ) : (
    <Card css={styles.skeleton}>
      <Stack px={2} py={1}>
        <Skeleton variant="text" width="3.5rem" height="2rem" />
        <Skeleton variant="text" width="5rem" height="1rem" />
        <Skeleton variant="text" width="8rem" height="1rem" />
      </Stack>
    </Card>
  );

type NextObservationCardProps = CardProps & {
  small?: boolean;
};

const NextObservationCard = ({ small = false, ...props }: NextObservationCardProps) => {
  const [listOpen, setListOpen] = useState(false);
  const { filters } = usePatientList();

  const mockData = useMemo(() => createMockNextObservations(), []);
  const mockResult = {
    data: mockData,
    isLoading: false,
  };

  const { data: upcomingObs, isLoading } = WORK_OFFLINE ? mockResult : useLiveNextObservations();

  if (isLoading) {
    return <ObservationSkeleton small={small} />;
  }

  // Find the next Observation that has a time due. Ignore others.
  const nextObs = (upcomingObs as any).find((obs: any) => obs.nextObsDue !== undefined);

  let title = "-";
  let detail = "None";
  let fillPercent = 0;

  if (nextObs) {
    const duration = getTimeFromNow(nextObs.nextObsDue as number);
    const minsDue = duration.asMinutes();

    // title = minsDue <= 0 ? "Now" : formatDuration(duration);
    title = formatDuration(duration);
    const firstName = nextObs.patient?.firstName || "";
    const lastName = nextObs.patient?.lastName || "";

    detail = formatName(firstName, lastName);
    fillPercent = ((minsDue as number) / OBSERVATION_INTERVAL) * 100;
  }

  const toggleList = () => setListOpen(!listOpen);
  const due = fillPercent <= 0;
  // load into a css var
  const fill = { ["--fill"]: `${fillPercent}%` } as React.CSSProperties;

  return (
    <>
      <Card {...props} css={styles.card} onClick={toggleList} style={fill} data-due={due} data-small={small}>
        {small ? (
          <>
            <HeartIcon />
            <Stack direction="row" alignItems="baseline" gap={1}>
              <Text
                variant="h6"
                color={due ? "error.main" : undefined}
              >
                { due ? title : `${title} Next obs due`}
              </Text>
              <PatientName encounter={nextObs} />
            </Stack>
          </>
        ) : (
          <>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Text
                variant="h4"
                flex={1}
                color={due ? "error.main" : undefined}
              >
                {title}
              </Text>
              <HeartIcon />
            </Stack>
            <Text variant="subtitle1" textTransform="uppercase">Next Obs Due</Text>
            <Text variant="body2" bold>{detail}</Text>
          </>
        )}
      </Card>

      <Drawer anchor="right" open={listOpen} onClose={toggleList}>
        <NextObservationsPanel
          observations={upcomingObs as PatientEncounter[]}
          filteredAreas={filters.area}
          onClose={toggleList}
        />
      </Drawer>
    </>
  );
};

export default NextObservationCard;
